import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Lock, Eye, Database, Users, Mail, Globe } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]">
      {/* Header */}
      <header className="border-b border-gray-800 bg-[#050505]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#10b981]" />
            <span className="text-white font-semibold">Bethereum Academy</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10b981]/10 border border-[#10b981]/20 mb-6">
            <Lock className="w-4 h-4 text-[#10b981]" />
            <span className="text-[#10b981] text-sm font-medium">Your Privacy Matters</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Privacy Policy</h1>
          <p className="text-gray-400">Last updated: January 2025</p>
        </div>

        <div className="space-y-8">
          {/* Educational Platform Notice */}
          <div className="bg-gradient-to-r from-[#10b981]/10 to-[#059669]/10 rounded-xl p-6 border border-[#10b981]/20">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-[#10b981]/20">
                <Globe className="w-6 h-6 text-[#10b981]" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg mb-2">Educational Platform</h3>
                <p className="text-gray-300">
                  Bethereum Academy is an educational platform for learning DeFi concepts. We operate on the 
                  <span className="text-[#10b981] font-semibold"> Sepolia testnet</span> - no real cryptocurrency 
                  or funds are involved in our learning exercises.
                </p>
              </div>
            </div>
          </div>

          {/* Information We Collect */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-5 h-5 text-[#10b981]" />
              <h2 className="text-xl font-semibold text-white">Information We Collect</h2>
            </div>
            <div className="space-y-4 text-gray-300">
              <div>
                <h3 className="text-white font-medium mb-2">Account Information</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-400">
                  <li>Email address (for account creation and communication)</li>
                  <li>Username (for leaderboards and community features)</li>
                  <li>Password (securely hashed, never stored in plain text)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Learning Data</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-400">
                  <li>Course progress and completion status</li>
                  <li>Quiz scores and achievements</li>
                  <li>Testnet transaction history (for educational purposes only)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Technical Data</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-400">
                  <li>Browser type and version</li>
                  <li>Device information</li>
                  <li>IP address and general location</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-5 h-5 text-[#10b981]" />
              <h2 className="text-xl font-semibold text-white">How We Use Your Information</h2>
            </div>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start gap-3">
                <span className="text-[#10b981]">•</span>
                <span>To provide and improve our educational services</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#10b981]">•</span>
                <span>To track your learning progress and issue certificates</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#10b981]">•</span>
                <span>To communicate important updates about courses and features</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#10b981]">•</span>
                <span>To prevent fraud and ensure platform security</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#10b981]">•</span>
                <span>To process payments for premium subscriptions (via Stripe)</span>
              </li>
            </ul>
          </section>

          {/* Data Protection */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-5 h-5 text-[#10b981]" />
              <h2 className="text-xl font-semibold text-white">Data Protection</h2>
            </div>
            <p className="text-gray-300 mb-4">
              We implement industry-standard security measures to protect your personal information:
            </p>
            <ul className="space-y-2 text-gray-400">
              <li className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#10b981]" />
                <span>SSL/TLS encryption for all data transmission</span>
              </li>
              <li className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#10b981]" />
                <span>Secure password hashing using bcrypt</span>
              </li>
              <li className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#10b981]" />
                <span>Regular security audits and updates</span>
              </li>
              <li className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#10b981]" />
                <span>Limited employee access to personal data</span>
              </li>
            </ul>
          </section>

          {/* Third-Party Services */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-5 h-5 text-[#10b981]" />
              <h2 className="text-xl font-semibold text-white">Third-Party Services</h2>
            </div>
            <p className="text-gray-300 mb-4">
              We use trusted third-party services to enhance your experience:
            </p>
            <ul className="space-y-2 text-gray-400">
              <li><strong className="text-white">Stripe:</strong> For secure payment processing</li>
              <li><strong className="text-white">Alchemy:</strong> For Sepolia testnet blockchain interactions</li>
              <li><strong className="text-white">Resend:</strong> For transactional emails</li>
            </ul>
            <p className="text-gray-400 mt-4 text-sm">
              These services have their own privacy policies, and we encourage you to review them.
            </p>
          </section>

          {/* Your Rights */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-5 h-5 text-[#10b981]" />
              <h2 className="text-xl font-semibold text-white">Your Rights</h2>
            </div>
            <p className="text-gray-300 mb-4">You have the right to:</p>
            <ul className="space-y-2 text-gray-400">
              <li>• Access your personal data</li>
              <li>• Correct inaccurate information</li>
              <li>• Request deletion of your account</li>
              <li>• Export your learning data</li>
              <li>• Opt-out of marketing communications</li>
            </ul>
          </section>

          {/* Contact */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-5 h-5 text-[#10b981]" />
              <h2 className="text-xl font-semibold text-white">Contact Us</h2>
            </div>
            <p className="text-gray-300">
              If you have questions about this Privacy Policy or wish to exercise your rights, 
              please contact us at:
            </p>
            <p className="text-[#10b981] mt-2">privacy@bethereum.academy</p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-800 text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Bethereum Academy. All rights reserved.
          </p>
          <div className="flex items-center justify-center gap-4 mt-4">
            <Link to="/terms" className="text-gray-400 hover:text-[#10b981] transition-colors text-sm">
              Terms of Service
            </Link>
            <span className="text-gray-700">•</span>
            <Link to="/about" className="text-gray-400 hover:text-[#10b981] transition-colors text-sm">
              About Safety
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Privacy;
